<template>
  <div class="region-select-container">
    <!-- Select de BANCA (apenas para ADMIN) -->
    <div v-if="isAdmin" class="searchable-select-wrapper">
      <div class="searchable-select" :class="{ 'is-disabled': loading }">
        <input
          type="text"
          class="searchable-select-input"
          name="ab-header-banca-search"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          role="combobox"
          aria-autocomplete="list"
          :placeholder="loading ? 'Carregando bancas...' : (selectedBanca ? selectedBanca.name : 'Todas as Bancas')"
          v-model="bancaSearchText"
          @focus="handleBancaFocus"
          @blur="handleBancaBlur"
          @input="filterBancas"
          :disabled="loading"
        />
        <span class="searchable-select-arrow">▼</span>
        <div v-if="showBancaDropdown && !loading" class="searchable-select-dropdown">
          <!-- Opção "Todas as Bancas" para ADMIN -->
          <div
            v-if="filteredBancas.length > 1"
            class="searchable-select-option all-regions-option"
            @mousedown.prevent="selectAllBancas"
            :class="{ 'is-selected': !selectedBanca }"
          >
            <i class="fas fa-building"></i>
            Todas as Bancas
          </div>
          
          <!-- Outras Bancas -->
          <div
            v-for="banca in filteredOtherBancas"
            :key="banca.id"
            class="searchable-select-option"
            @mousedown.prevent="selectBanca(banca)"
            :class="{ 'is-selected': selectedBanca && selectedBanca.id === banca.id, 'is-disabled': selectedBanca && onlineBanca && selectedBanca.id === onlineBanca.id }"
          >
            <i v-if="selectedBanca && onlineBanca && selectedBanca.id === onlineBanca.id" class="fas fa-lock" style="margin-right: 8px; color: #9ca3af;"></i>
            {{ banca.name }}
          </div>
          
          <!-- Separador e Checkbox para BANCA ONLINE (movido para baixo) -->
          <div v-if="onlineBanca" class="banca-online-section">
            <div class="banca-online-header">
              <label class="banca-online-checkbox-label">
                <input
                  type="checkbox"
                  class="banca-online-checkbox"
                  :checked="selectedBanca && selectedBanca.id === onlineBanca.id"
                  @change="toggleOnlineBanca"
                  @mousedown.prevent
                />
                <span class="banca-online-name">
                  <i class="fas fa-globe"></i>
                  {{ onlineBanca.name }}
                </span>
              </label>
            </div>
          </div>
          
          <div v-if="filteredOtherBancas.length === 0 && !onlineBanca" class="searchable-select-option no-results">
            Nenhuma banca encontrada
          </div>
        </div>
      </div>
    </div>

    <!-- Select de Região -->
    <div class="searchable-select-wrapper">
      <div class="searchable-select" :class="{ 'is-disabled': loading, 'alert-select-region': getSelectedRegionClass() }">
        <input
          type="text"
          class="searchable-select-input"
          name="ab-header-region-search"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          role="combobox"
          aria-autocomplete="list"
          :placeholder="loading || initialLoad ? 'Carregando regiões...' : (selectedRegion ? selectedRegion.name : ((isAdmin || isBanca) ? 'Todas as Regiões' : 'Nenhuma região selecionada'))"
          v-model="regionSearchText"
          @focus="handleRegionFocus"
          @blur="handleRegionBlur"
          @input="filterRegions"
          :disabled="loading"
        />
        <span class="searchable-select-arrow">▼</span>
        <div v-if="showRegionDropdown && !loading" class="searchable-select-dropdown">
          <!-- Opção "Todas as Regiões" para ADMIN e BANCA -->
          <div
            v-if="(isAdmin || isBanca) && filteredRegions.length > 1"
            class="searchable-select-option all-regions-option"
            @mousedown.prevent="selectAllRegions"
            :class="{ 'is-selected': !selectedRegion }"
          >
            <i class="fas fa-globe"></i>
            Todas as Regiões
          </div>
          
          <div
            v-for="region in filteredRegions"
            :key="region.id"
            class="searchable-select-option"
            @mousedown.prevent="selectRegion(region)"
            :class="{ 'is-selected': selectedRegion && selectedRegion.id === region.id }"
          >
            {{ region.name }}
          </div>
          <div v-if="filteredRegions.length === 0" class="searchable-select-option no-results">
            Nenhuma região encontrada
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";
import { useRegion } from "@/composables/useRegion";

export default {
  setup() {
    const { setRegionReady, setCurrentRegion, resetRegion } = useRegion();
    return { setRegionReady, setCurrentRegion, resetRegion };
  },
  data: () => ({
    regions: [],
    selectedRegion: null,
    currentUser: null,
    bancas: [],
    selectedBanca: null,
    onlineBanca: null,
    otherBancas: [],
    loading: false,
    userBancaId: null,
    initialLoad: true,
    // UUID da banca ONLINE
    ONLINE_BANCA_UUID: "e9c624bd-8403-4b8c-9ba1-ca4954833e93",
    // Propriedades para busca
    bancaSearchText: '',
    regionSearchText: '',
    showBancaDropdown: false,
    showRegionDropdown: false,
    filteredBancas: [],
    filteredOtherBancas: [],
    filteredRegions: [],
  }),
  computed: {
    isAdmin() {
      const role = this.currentUser ? parseInt(this.currentUser.role) : null;
      return this.currentUser && role === ROLES.ADMIN;
    },

    isBanca() {
      const role = this.currentUser ? parseInt(this.currentUser.role) : null;
      return this.currentUser && role === ROLES.BANCA;
    }
  },
  async mounted() {
    // Resetar estado da região ao montar
    this.resetRegion();

    // Aguardar um pouco para garantir que o localStorage foi carregado
    await new Promise(resolve => setTimeout(resolve, 100));

    this.currentUser = JSON.parse(localStorage.getItem("user"));

    if (!this.currentUser) {
      // Se não há usuário, aguardar mais tempo
      await new Promise(resolve => setTimeout(resolve, 500));
      this.currentUser = JSON.parse(localStorage.getItem("user"));
    }

    // Verificar e limpar dados inconsistentes
    this.validateStoredData();

    if (this.isAdmin) {
      await this.loadBancas();
    } else {
      // Para usuários não-admin, carregar o BancaId primeiro
      await this.loadUserBancaId();
      await this.searchRegions();
    }

    // Marcar que o carregamento inicial foi concluído
    this.initialLoad = false;

    // Se há uma região selecionada, marcar como pronta
    if (this.selectedRegion) {
      this.setCurrentRegion(this.selectedRegion);
      this.setRegionReady(true);
    }

    window.addEventListener('headerSelectsNeedRefresh', this.handleHeaderSelectsRefresh);
  },
  beforeUnmount() {
    window.removeEventListener('headerSelectsNeedRefresh', this.handleHeaderSelectsRefresh);
  },
  methods: {
    async handleHeaderSelectsRefresh(event) {
      const detail = event?.detail || {};
      const refreshBancas = detail.bancas !== false;
      const refreshRegions = detail.regions !== false;

      if (refreshBancas && this.isAdmin) {
        await this.loadBancas();
      }
      if (refreshRegions) {
        await this.searchRegions();
      }
    },

    async loadBancas() {
      try {
        this.loading = true;
        // Carregar TODAS as bancas (ativas e inativas) para poder gerenciar o status
        const response = await mainService.searchBancas({});
        const allBancas = response.data || [];
        
        // Separar BANCA ONLINE das outras (verificar por UUID ou nome)
        this.onlineBanca = allBancas.find(b => 
          b.id === this.ONLINE_BANCA_UUID || 
          (b.name && b.name.toUpperCase().includes('ONLINE'))
        ) || null;
        
        this.otherBancas = allBancas.filter(b => 
          b.id !== this.ONLINE_BANCA_UUID && 
          (!b.name || !b.name.toUpperCase().includes('ONLINE'))
        );
        
        this.bancas = allBancas;
        this.filteredBancas = this.bancas;
        this.filteredOtherBancas = this.otherBancas;
        
        // Verificar se há uma BANCA previamente selecionada
        const savedBanca = localStorage.getItem("selectedBanca");
        if (savedBanca) {
          const savedBancaData = JSON.parse(savedBanca);
          const foundBanca = this.bancas.find(b => b.id === savedBancaData.id);
          if (foundBanca) {
            this.selectedBanca = foundBanca;
            this.bancaSearchText = foundBanca.name;
            
            // Se a banca selecionada é a ONLINE, garantir que excludeOnlineBanca está removido
            if (this.onlineBanca && foundBanca.id === this.onlineBanca.id) {
              localStorage.removeItem("excludeOnlineBanca");
            }
          } else {
            // BANCA não encontrada, limpar dados salvos
            localStorage.removeItem("selectedBanca");
            localStorage.removeItem("currentRegion");
          }
        }
        
        // Sempre carregar regiões para ADMIN (mesmo sem banca selecionada = todas as regiões)
        await this.searchRegions();
      } catch (error) {
        toast.error("Erro ao carregar bancas: " + handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },

    async onBancaChange() {
      // Resetar estado da região
      this.resetRegion();

      if (this.selectedBanca) {
        // Salvar BANCA selecionada
        localStorage.setItem("selectedBanca", JSON.stringify(this.selectedBanca));
        
        // Se não é a banca ONLINE, marcar para excluir ONLINE
        if (this.onlineBanca && this.selectedBanca.id !== this.onlineBanca.id) {
          localStorage.setItem("excludeOnlineBanca", "true");
        } else {
          localStorage.removeItem("excludeOnlineBanca");
        }

        // Resetar região selecionada
        this.selectedRegion = null;
        localStorage.removeItem("currentRegion");

        // Disparar evento para outros componentes
        window.dispatchEvent(new CustomEvent('bancaChanged', {
          detail: { 
            banca: this.selectedBanca,
            excludeOnline: this.onlineBanca && this.selectedBanca.id !== this.onlineBanca.id
          }
        }));

        // Recarregar a página apenas para mudança de banca (necessário para ADMIN)
        location.reload();
      } else {
        // BANCA desmarcada (Todas as Bancas), limpar tudo e excluir ONLINE
        localStorage.removeItem("selectedBanca");
        localStorage.setItem("excludeOnlineBanca", "true");
        localStorage.removeItem("currentRegion");
        this.selectedRegion = null;
        this.regions = [];

        // Disparar evento para outros componentes
        window.dispatchEvent(new CustomEvent('bancaChanged', {
          detail: { banca: null, excludeOnline: true }
        }));

        // Recarregar a página apenas para mudança de banca (necessário para ADMIN)
        location.reload();
      }
    },

    async loadUserBancaId() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserBancaId(this.currentUser.id);
        this.userBancaId = response.data;
      } catch (error) {
        console.error('Erro ao carregar BancaId do usuário:', error);
        this.userBancaId = null;
      }
    },

    async searchRegions() {
      this.loading = true;
      try {
        if (this.isAdmin) {
          if (this.selectedBanca) {
            // Para ADMIN: buscar regiões da BANCA selecionada (incluindo ONLINE)
            const allRegionsResponse = await mainService.searchRegions();
            const allRegions = allRegionsResponse.data || [];
            
            // Filtrar regiões da banca selecionada usando BancaId
            this.regions = allRegions.filter(region => region.bancaId === this.selectedBanca.id);
          } else {
            // Se não há BANCA selecionada (Todas as Bancas), mostrar TODAS as regiões EXCETO da banca ONLINE
            const allRegionsResponse = await mainService.searchRegions();
            const allRegions = allRegionsResponse.data || [];
            
            // Excluir regiões da banca ONLINE quando "Todas as Bancas" está selecionado
            if (this.onlineBanca) {
              this.regions = allRegions.filter(region => region.bancaId !== this.onlineBanca.id);
            } else {
              this.regions = allRegions;
            }
          }
        } else if (this.isBanca) {
          // Para BANCA: buscar regiões onde BancaId corresponde ao userBancaId
          const allRegionsResponse = await mainService.searchRegions();
          const allRegions = allRegionsResponse.data || [];
          
          this.regions = allRegions.filter(region => region.bancaId === this.userBancaId);
        } else {
          // Para GERENTE e outros: buscar regiões do usuário (principal + adicionais)
          const [userRegionsResponse, allRegionsResponse] = await Promise.all([
            mainService.getUserRegions(this.currentUser.id),
            mainService.searchRegions()
          ]);
          
          const userRegions = userRegionsResponse.data || [];
          const allRegions = allRegionsResponse.data || [];
          
          // Incluir região principal se existir
          const regionIds = userRegions.map(r => r.id);
          if (this.currentUser.regionId && !regionIds.includes(this.currentUser.regionId)) {
            regionIds.push(this.currentUser.regionId);
          }
          
          // Filtrar apenas as regiões do usuário
          this.regions = allRegions.filter(region => regionIds.includes(region.id));
        }
        
        // Inicializar lista filtrada
        this.filteredRegions = this.regions;
        
        // Verificar se há região salva e se ela ainda é válida
        const savedRegion = localStorage.getItem("currentRegion");
        if (savedRegion) {
          try {
            this.selectedRegion = JSON.parse(savedRegion);
            this.regionSearchText = this.selectedRegion.name;
            
            // Se a região selecionada não está mais disponível, resetar
            if (this.selectedRegion && !this.regions.find(r => r.id === this.selectedRegion.id)) {
              this.selectedRegion = null;
              this.regionSearchText = '';
              localStorage.removeItem("currentRegion");
            }
          } catch (e) {
            this.selectedRegion = null;
            this.regionSearchText = '';
            localStorage.removeItem("currentRegion");
          }
        } else {
          this.selectedRegion = null;
          this.regionSearchText = '';
        }
        
        // Para usuários não-admin, selecionar automaticamente região
        if (!this.isAdmin) {
          await this.autoSelectRegion();
        }
      } catch (error) {
        console.error("Error in searchRegions:", error);
        // Só mostrar toast de erro se não está no processo inicial de carregamento
        if (this.regions.length === 0) {
          // toast.error(handleErrorMessage(error));
        }
      } finally {
        this.loading = false;
      }
    },

    async autoSelectRegion() {
      // Se já há uma região selecionada, não fazer nada
      if (this.selectedRegion) return;
      
      // Para ADMIN e BANCA com múltiplas regiões, deixar como "Todas as Regiões"
      if ((this.isAdmin || this.isBanca) && this.regions.length > 1) {
        // Não selecionar nenhuma região, permitir ver todas
        this.setRegionReady(true);
        return;
      }
      
      let regionToSelect = null;
      
      // Se há apenas uma região disponível, selecioná-la automaticamente
      if (this.regions.length === 1) {
        regionToSelect = this.regions[0];
      } else if (this.currentUser && this.currentUser.regionId && this.regions.length > 0) {
        // Para usuários não-admin/não-banca, tentar selecionar automaticamente a região principal do usuário
        const userMainRegion = this.regions.find(r => r.id === this.currentUser.regionId);
        if (userMainRegion) {
          regionToSelect = userMainRegion;
        }
      }
      
      if (regionToSelect) {
        this.selectedRegion = regionToSelect;
        this.regionSearchText = regionToSelect.name;
        localStorage.setItem("currentRegion", JSON.stringify(this.selectedRegion));

        // Atualizar composable
        this.setCurrentRegion(this.selectedRegion);
        this.setRegionReady(true);

        // Disparar evento para outros componentes
        window.dispatchEvent(new CustomEvent('regionAutoSelected', {
          detail: { region: this.selectedRegion }
        }));

        // Disparar evento storage manualmente para páginas que escutam storage
        window.dispatchEvent(new StorageEvent('storage', {
          key: 'currentRegion',
          newValue: JSON.stringify(this.selectedRegion),
          oldValue: null,
          storageArea: localStorage
        }));

        // Para auto-seleção, não fazer reload pois é na inicialização
        // Os componentes que escutam os eventos irão se atualizar
      }
    },

    setGlobalRegion() {
      if (this.selectedRegion) {
        localStorage.setItem("currentRegion", JSON.stringify(this.selectedRegion));
        this.setCurrentRegion(this.selectedRegion);
        this.setRegionReady(true);
      } else {
        localStorage.removeItem("currentRegion");
        this.resetRegion();
      }

      // Disparar evento personalizado para outros componentes
      window.dispatchEvent(new CustomEvent('regionChanged', {
        detail: { region: this.selectedRegion }
      }));

      // Disparar evento storage manualmente para páginas que escutam storage
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'currentRegion',
        newValue: this.selectedRegion ? JSON.stringify(this.selectedRegion) : null,
        oldValue: null,
        storageArea: localStorage
      }));

      // Aguardar um pouco para que os eventos sejam processados e então recarregar
      setTimeout(() => {
        location.reload();
      }, 50);
    },

    validateStoredData() {
      try {
        // Para usuários não-admin, limpar dados de banca que não deveriam existir
        if (!this.isAdmin) {
          const savedBanca = localStorage.getItem("selectedBanca");
          if (savedBanca) {
            localStorage.removeItem("selectedBanca");
          }
        }
        
        // Verificar se os dados armazenados são válidos
        const savedRegion = localStorage.getItem("currentRegion");
        if (savedRegion) {
          try {
            const regionData = JSON.parse(savedRegion);
            if (!regionData || !regionData.id || !regionData.name) {
              localStorage.removeItem("currentRegion");
            }
          } catch (e) {
            localStorage.removeItem("currentRegion");
          }
        }
        
        const savedBanca = localStorage.getItem("selectedBanca");
        if (savedBanca && this.isAdmin) {
          try {
            const bancaData = JSON.parse(savedBanca);
            if (!bancaData || !bancaData.id || !bancaData.name) {
              localStorage.removeItem("selectedBanca");
              localStorage.removeItem("currentRegion");
            }
          } catch (e) {
            localStorage.removeItem("selectedBanca");
            localStorage.removeItem("currentRegion");
          }
        }
      } catch (error) {
        console.error('Erro ao validar dados armazenados:', error);
      }
    },

    filterBancas() {
      const search = this.bancaSearchText.toLowerCase().trim();
      if (!search) {
        this.filteredOtherBancas = this.otherBancas;
      } else {
        this.filteredOtherBancas = this.otherBancas.filter(banca =>
          banca.name.toLowerCase().includes(search)
        );
      }
      this.showBancaDropdown = true;
    },

    async toggleOnlineBanca(event) {
      const isChecked = event.target.checked;
      
      if (!this.onlineBanca) return;
      
      try {
        this.loading = true;
        
        if (isChecked) {
          // Apenas seleção visual - não altera o sistema
          // Selecionar a BANCA ONLINE apenas no frontend
          this.selectedBanca = { ...this.onlineBanca };
          this.bancaSearchText = this.onlineBanca.name;
          localStorage.setItem("selectedBanca", JSON.stringify(this.selectedBanca));
          
          // Limpar região selecionada quando seleciona ONLINE
          this.selectedRegion = null;
          localStorage.removeItem("currentRegion");
          this.resetRegion();
          
          // Marcar que ONLINE está selecionada (não excluir)
          localStorage.removeItem("excludeOnlineBanca");
          
          // Disparar evento para outros componentes
          window.dispatchEvent(new CustomEvent('bancaChanged', {
            detail: { banca: this.selectedBanca, excludeOnline: false }
          }));
          
          // Recarregar regiões da banca ONLINE
          await this.searchRegions();
          
          // Recarregar página para aplicar filtros visuais
          setTimeout(() => {
            location.reload();
          }, 50);
        } else {
          // Desmarcar ONLINE - apenas limpar seleção visual
          if (this.selectedBanca && this.selectedBanca.id === this.onlineBanca.id) {
            this.selectedBanca = null;
            this.bancaSearchText = '';
            localStorage.removeItem("selectedBanca");
            localStorage.removeItem("currentRegion");
            this.resetRegion();
            
            // Marcar para excluir ONLINE quando desmarcar
            localStorage.setItem("excludeOnlineBanca", "true");
            
            // Disparar evento para outros componentes
            window.dispatchEvent(new CustomEvent('bancaChanged', {
              detail: { banca: null, excludeOnline: true }
            }));
            
            // Recarregar regiões
            await this.searchRegions();
            
            // Recarregar página para aplicar filtros visuais
            setTimeout(() => {
              location.reload();
            }, 50);
          }
        }
        
      } catch (error) {
        toast.error("Erro ao selecionar banca: " + handleErrorMessage(error));
        // Reverter o checkbox em caso de erro
        event.target.checked = !isChecked;
      } finally {
        this.loading = false;
      }
    },

    filterRegions() {
      const search = this.regionSearchText.toLowerCase().trim();
      if (!search) {
        this.filteredRegions = this.regions;
      } else {
        this.filteredRegions = this.regions.filter(region =>
          region.name.toLowerCase().includes(search)
        );
      }
      this.showRegionDropdown = true;
    },

    async selectBanca(banca) {
      // Apenas seleção visual - não altera o sistema
      // Se estava selecionada a BANCA ONLINE, apenas trocar a seleção visual
      this.selectedBanca = banca;
      this.bancaSearchText = banca.name;
      this.showBancaDropdown = false;
      
      // Se não é a banca ONLINE, marcar para excluir ONLINE
      if (this.onlineBanca && banca.id !== this.onlineBanca.id) {
        localStorage.setItem("excludeOnlineBanca", "true");
      } else {
        localStorage.removeItem("excludeOnlineBanca");
      }
      
      this.onBancaChange();
    },

    selectAllBancas() {
      // Desmarcar a banca selecionada para mostrar todas (excluindo ONLINE)
      this.selectedBanca = null;
      this.bancaSearchText = '';
      this.showBancaDropdown = false;
      
      // Limpar do localStorage
      localStorage.removeItem("selectedBanca");
      localStorage.removeItem("currentRegion");
      
      // Marcar que "Todas as Bancas" está selecionado (excluindo ONLINE)
      localStorage.setItem("excludeOnlineBanca", "true");
      
      // Resetar região também
      this.selectedRegion = null;
      this.resetRegion();
      
      // Recarregar regiões (vai excluir ONLINE automaticamente)
      this.searchRegions();
      
      // Disparar evento para outros componentes
      window.dispatchEvent(new CustomEvent('bancaChanged', {
        detail: { banca: null, excludeOnline: true }
      }));

      // Recarregar a página
      setTimeout(() => {
        location.reload();
      }, 50);
    },

    selectRegion(region) {
      this.selectedRegion = region;
      this.regionSearchText = region.name;
      this.showRegionDropdown = false;
      this.setGlobalRegion();
    },

    selectAllRegions() {
      // Desmarcar a região selecionada para mostrar todas
      this.selectedRegion = null;
      this.regionSearchText = '';
      this.showRegionDropdown = false;
      
      // Limpar do localStorage
      localStorage.removeItem("currentRegion");
      this.resetRegion();
      
      // Disparar evento personalizado para outros componentes
      window.dispatchEvent(new CustomEvent('regionChanged', {
        detail: { region: null }
      }));

      // Disparar evento storage manualmente para páginas que escutam storage
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'currentRegion',
        newValue: null,
        oldValue: null,
        storageArea: localStorage
      }));

      // Aguardar um pouco para que os eventos sejam processados e então recarregar
      setTimeout(() => {
        location.reload();
      }, 50);
    },

    preventBrowserAutofill(event) {
      // Chrome ignora autocomplete="off" em vários casos; o readonly
      // temporário impede o popup de e-mail/endereço ao focar o select.
      const input = event?.target;
      if (!input) return;
      input.setAttribute('readonly', 'readonly');
      requestAnimationFrame(() => {
        input.removeAttribute('readonly');
      });
    },

    handleBancaFocus(event) {
      this.preventBrowserAutofill(event);
      this.showBancaDropdown = true;
      // Mostrar todas as outras bancas quando o dropdown é aberto
      this.filteredOtherBancas = this.otherBancas;
      // Limpar o texto de busca para mostrar todas as opções
      if (this.selectedBanca) {
        this.bancaSearchText = '';
      }
    },

    handleRegionFocus(event) {
      this.preventBrowserAutofill(event);
      this.showRegionDropdown = true;
      // Mostrar todas as regiões quando o dropdown é aberto
      this.filteredRegions = this.regions;
      // Limpar o texto de busca para mostrar todas as opções
      if (this.selectedRegion) {
        this.regionSearchText = '';
      }
    },

    handleBancaBlur() {
      // Usar setTimeout para permitir que o clique no dropdown seja processado primeiro
      setTimeout(() => {
        this.showBancaDropdown = false;
        // Restaurar o texto se houver uma banca selecionada
        if (this.selectedBanca) {
          this.bancaSearchText = this.selectedBanca.name;
        } else {
          this.bancaSearchText = '';
        }
      }, 200);
    },

    handleRegionBlur() {
      // Usar setTimeout para permitir que o clique no dropdown seja processado primeiro
      setTimeout(() => {
        this.showRegionDropdown = false;
        // Restaurar o texto se houver uma região selecionada
        if (this.selectedRegion) {
          this.regionSearchText = this.selectedRegion.name;
        } else {
          this.regionSearchText = '';
        }
      }, 200);
    },

    getSelectedRegionClass() {
      // Para ADMIN e BANCA, não mostrar alerta quando nenhuma região está selecionada
      // pois eles podem escolher "Todas as Regiões"
      if ((this.isAdmin || this.isBanca) && !this.selectedRegion) {
        return "";
      }
      return this.selectedRegion == null ? "alert-select-region" : "";
    },
  },
};
</script>

<style scoped>
.region-select-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.searchable-select-wrapper {
  position: relative;
}

.searchable-select {
  position: relative;
  width: 200px;
}

.searchable-select-input {
  width: 100%;
  padding: 8px 32px 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-primary);
  color: var(--text-primary);
  font-size: 14px;
  transition: var(--transition);
  box-sizing: border-box;
}

.searchable-select-input:focus {
  outline: none;
  border-color: var(--accent-primary);
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.searchable-select-input:disabled {
  background-color: #f9fafb;
  color: #9ca3af;
  cursor: not-allowed;
}

[data-theme="dark"] .searchable-select-input:disabled {
  background-color: #374151;
  color: #6b7280;
}

.searchable-select-arrow {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--text-secondary);
  font-size: 10px;
  transition: transform 0.2s;
}

.searchable-select:focus-within .searchable-select-arrow {
  transform: translateY(-50%) rotate(180deg);
}

.searchable-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  max-height: 250px;
  overflow-y: auto;
  background-color: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  box-sizing: border-box;
}

[data-theme="dark"] .searchable-select-dropdown {
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.searchable-select-option {
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.15s;
  color: var(--text-primary);
  font-size: 14px;
}

.searchable-select-option:hover {
  background-color: var(--bg-secondary);
}

.searchable-select-option.is-selected {
  background-color: var(--accent-primary);
  color: white;
}

.searchable-select-option.is-selected:hover {
  background-color: var(--accent-primary);
  opacity: 0.9;
}

.searchable-select-option.no-results {
  color: var(--text-secondary);
  cursor: default;
  font-style: italic;
}

.searchable-select-option.no-results:hover {
  background-color: transparent;
}

.searchable-select-option.all-regions-option {
  font-weight: 600;
  border-bottom: 1px solid var(--border-color);
  background-color: var(--bg-secondary);
}

.searchable-select-option.all-regions-option i {
  margin-right: 8px;
  color: var(--accent-primary);
}

.searchable-select-option.all-regions-option:hover {
  background-color: var(--accent-primary);
  color: white;
}

.searchable-select-option.all-regions-option:hover i {
  color: white;
}

.searchable-select-option.all-regions-option.is-selected {
  background-color: var(--accent-primary);
  color: white;
}

.searchable-select-option.all-regions-option.is-selected i {
  color: white;
}

.searchable-select-option.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: var(--bg-secondary);
}

.searchable-select-option.is-disabled:hover {
  background-color: var(--bg-secondary);
}

.banca-online-section {
  border-top: 2px solid var(--border-color);
  border-bottom: 2px solid var(--border-color);
  padding: 12px;
  margin: 8px 0;
  background-color: var(--bg-secondary);
}

.banca-online-header {
  margin-bottom: 8px;
}

.banca-online-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  color: var(--text-primary);
}

.banca-online-checkbox {
  margin-right: 10px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--accent-primary);
}

.banca-online-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.banca-online-name i {
  color: var(--accent-primary);
}

.searchable-select.alert-select-region .searchable-select-input {
  border-color: #ef4444;
  animation: blink 1s infinite;
}

@keyframes blink {
  50% {
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.3);
  }
}

/* Mobile responsivo */
@media (max-width: 768px) {
  .region-select-container {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }
  
  .searchable-select {
    width: 100%;
    min-width: 180px;
  }
}
</style>
